/**
 * MyGymsTab Component
 *
 * Displays role-aware gym listings: owned gyms (for owners), setter gyms (for setters),
 * and followed gyms (for everyone). Shows home gym indicator and provides navigation
 * links for finding and registering gyms.
 *
 * @example
 * ```tsx
 * <MyGymsTab userRole="owner" ownedGymIds={['gym1']} setterGymIds={[]} />
 * ```
 */

import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { useGyms } from '@/hooks/useGyms';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

/**
 * Props for MyGymsTab component
 */
interface MyGymsTabProps {
  /** User's role in the application */
  userRole: 'climber' | 'setter' | 'owner' | 'admin';
  /** IDs of gyms the user owns */
  ownedGymIds: string[];
  /** IDs of gyms where the user is a setter */
  setterGymIds: string[];
}

/**
 * MyGymsTab displays role-aware gym listings with owned, setter, and followed sections
 */
export function MyGymsTab({ ownedGymIds, setterGymIds }: MyGymsTabProps) {
  const { memberships, loading: membershipsLoading } = useGymMemberships();
  const { gyms, loading: gymsLoading } = useGyms();
  const [unfollowingGymIds, setUnfollowingGymIds] = useState<Set<string>>(new Set());
  const [unfollowError, setUnfollowError] = useState<string | null>(null);

  /**
   * Handle unfollowing a gym
   */
  const handleUnfollow = async (e: React.MouseEvent, gymId: string) => {
    e.preventDefault();
    e.stopPropagation();

    setUnfollowingGymIds((prev) => new Set(prev).add(gymId));
    setUnfollowError(null);

    try {
      const functions = getFirebaseFunctions();
      const callable = httpsCallable(functions, 'unfollowGym');
      await callable({ gymId });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unfollow gym';
      setUnfollowError(errorMessage);
      console.error('Unfollow error:', err);
    } finally {
      setUnfollowingGymIds((prev) => {
        const next = new Set(prev);
        next.delete(gymId);
        return next;
      });
    }
  };

  if (membershipsLoading || gymsLoading) {
    return <LoadingSpinner />;
  }

  const ownedGyms = gyms.filter((g) => ownedGymIds.includes(g.id));
  const setterGyms = gyms.filter((g) => setterGymIds.includes(g.id) && !ownedGymIds.includes(g.id));
  const followedGyms = memberships.filter(
    (m) => !ownedGymIds.includes(m.gymId) && !setterGymIds.includes(m.gymId)
  );

  return (
    <div style={containerStyle}>
      {/* Owned Gyms Section (for owners) */}
      {ownedGyms.length > 0 && (
        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Gyms You Own</h3>
          {ownedGyms.map((gym) => (
            <Link key={gym.id} to={`/gyms/${gym.id}/settings`} style={gymCardStyle}>
              <div style={gymInfoStyle}>
                <span style={gymNameStyle}>{gym.name}</span>
                <span style={gymMetaStyle}>
                  {gym.city}, {gym.state}
                </span>
              </div>
              <SettingsIcon sx={actionIconStyle} />
            </Link>
          ))}
        </section>
      )}

      {/* Setter Gyms Section (for setters) */}
      {setterGyms.length > 0 && (
        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Gyms You Set At</h3>
          {setterGyms.map((gym) => (
            <Link key={gym.id} to={`/gyms/${gym.id}`} style={gymCardStyle}>
              <div style={gymInfoStyle}>
                <span style={gymNameStyle}>{gym.name}</span>
                <span style={gymMetaStyle}>
                  {gym.city}, {gym.state}
                </span>
              </div>
              <PlaceIcon sx={actionIconStyle} />
            </Link>
          ))}
        </section>
      )}

      {/* Followed Gyms Section (everyone) */}
      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Gyms You Follow</h3>
        {unfollowError && <div style={errorBannerStyle}>{unfollowError}</div>}
        {followedGyms.length > 0 ? (
          followedGyms.map((membership) => {
            const gym = gyms.find((g) => g.id === membership.gymId);
            if (!gym) return null;
            const isUnfollowing = unfollowingGymIds.has(gym.id);
            return (
              <div key={gym.id} style={gymCardWithActionsStyle}>
                <Link to={`/gyms/${gym.id}`} style={gymCardLinkStyle}>
                  <div style={gymInfoStyle}>
                    <span style={gymNameStyle}>{gym.name}</span>
                    <span style={gymMetaStyle}>
                      {gym.city}, {gym.state}
                    </span>
                  </div>
                  {membership.isHomeGym && <span style={homeGymBadgeStyle}>★</span>}
                </Link>
                <button
                  onClick={(e) => handleUnfollow(e, gym.id)}
                  disabled={isUnfollowing}
                  style={{
                    ...unfollowButtonStyle,
                    ...(isUnfollowing ? loadingButtonStyle : {}),
                  }}
                  type="button"
                  aria-label={`Unfollow ${gym.name}`}
                >
                  {isUnfollowing ? '...' : <CloseIcon sx={{ fontSize: 18 }} />}
                </button>
              </div>
            );
          })
        ) : (
          <p style={emptyTextStyle}>{"You haven't followed any gyms yet."}</p>
        )}
        <Link to="/gyms" style={actionButtonStyle}>
          + Find More Gyms
        </Link>
      </section>
    </div>
  );
}

const containerStyle: CSSProperties = {
  padding: 'var(--spacing-md)',
};

const sectionStyle: CSSProperties = {
  marginBottom: 'var(--spacing-lg)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  marginBottom: 'var(--spacing-sm)',
};

const gymCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-md)',
  marginBottom: 'var(--spacing-sm)',
  textDecoration: 'none',
  color: 'inherit',
};

const gymInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
};

const gymNameStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const gymMetaStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
};

const actionIconStyle = {
  color: 'var(--color-text-secondary)',
};

const homeGymBadgeStyle: CSSProperties = {
  color: 'var(--color-primary)',
  fontSize: 'var(--font-size-lg)',
};

const actionButtonStyle: CSSProperties = {
  display: 'block',
  textAlign: 'center',
  padding: 'var(--spacing-sm)',
  color: 'var(--color-primary)',
  textDecoration: 'none',
};

const emptyTextStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
  padding: 'var(--spacing-md)',
};

const gymCardWithActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
  marginBottom: 'var(--spacing-sm)',
};

const gymCardLinkStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flex: 1,
  padding: 'var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-md)',
  textDecoration: 'none',
  color: 'inherit',
};

const unfollowButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
  transition: 'background-color 0.2s, color 0.2s',
};

const loadingButtonStyle: CSSProperties = {
  opacity: 0.7,
  cursor: 'not-allowed',
};

const errorBannerStyle: CSSProperties = {
  padding: 'var(--spacing-sm) var(--spacing-md)',
  marginBottom: 'var(--spacing-sm)',
  backgroundColor: 'var(--color-error-bg, #fef2f2)',
  color: 'var(--color-error, #ef4444)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
};

export default MyGymsTab;
